import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DataProvider, Pagination, QButton, Sorting, useCurrentUser } from '@qualio/ui-components';
import { EventStep, UserTask } from '../../../types';
import StepContainer from './StepContainer';
import TaskContent from './TaskContent';
import { useEventPermissions } from '../../../hooks';
import DigitalSignatureModal from '../../DigitalSignatureModal/DigitalSignatureModal';
import { DigitalSignature, DigitalSignatureInput } from '../../../types/digitalSignature';
import { buildEventQri } from '../../../utils/qriUtils';
import issueStepsApi from '../../../api/issueSteps.api';
import { isStepNotCompleted } from '../../../utils/eventUtils';
import * as DisplayStrings from '../../../displayStrings';
import { useRevertIssueStep } from '../../../hooks/useRevertIssueStep';
import tasksApi from '../../../api/tasks.api';

type TasksStepProps = {
  isResolutionStepForApproval: boolean;
  step: EventStep;
  refetch: () => void;
  eventDueDate?: string;
};

const getOrderByParam = (sort: Sorting.SortingContextValue | null): string | undefined => {
  if (sort?.column) {
    return sort.descending ? `-${sort.column}` : sort.column;
  }
};

const TasksStep: React.FC<TasksStepProps> = ({ isResolutionStepForApproval, step, refetch, eventDueDate }) => {
  const { t } = useTranslation();
  const stepId = step.id;
  const stepStatus = step.status;
  const stepNotCompleted = isStepNotCompleted(stepStatus);

  const { companyId } = useCurrentUser();
  const { Provider: PaginationProvider, offset, limit } = Pagination.useLimitOffset();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCompleteTaskStep, canRevertStep, isEventOwner, canCreateTask } = useEventPermissions();
  const { mutateAsync: revertIssueStepMutate } = useRevertIssueStep(step.issue_id, step.id);

  const [isCompleteStepModalOpen, setIsCompleteStepModalOpen] = useState(false);
  const [isRevertStepModalOpen, setIsRevertStepModalOpen] = useState(false);

  const handleTasksListApiCall = useCallback(
    ({ pageParam }: { pageParam: DataProvider.PageParams }) => {
      const orderBy = getOrderByParam(pageParam.sorting);
      return tasksApi
        .getAllTasksMappedToUserTaskResponse(companyId, {
          filter_by_issue_step_ids: String(stepId),
          status: ['open', 'closed_success', 'closed_fail'],
          ...(orderBy && { order_by: orderBy }),
          offset,
          limit,
        })
        .then((data) => {
          return {
            itemCount: data?.total ?? 0,
            data: data?.items ?? [],
          };
        });
    },
    [companyId, offset, limit, stepId],
  );

  const handleOpenTask = useCallback(
    (task: UserTask) => {
      searchParams.set('task_id', String(task.id));
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const onCompleteTask = useCallback((task: UserTask) => {
    window.dispatchEvent(
      new CustomEvent('QCompleteTask-open', {
        detail: { taskId: String(task.id) },
      }),
    );
  }, []);

  const onReopenTask = useCallback(
    (task: UserTask) => {
      window.dispatchEvent(
        new CustomEvent('QReopenTask-open', {
          detail: { taskId: String(task.id), maxDueDate: eventDueDate },
        }),
      );
    },
    [eventDueDate],
  );

  const handleCompleteStep = async (payload: DigitalSignature) => {
    await issueStepsApi.signOff(companyId, step.issue_id, step.id, payload);
    refetch();
  };

  const handleRevertStep = useCallback(
    async (payload: DigitalSignature) => {
      await revertIssueStepMutate(payload);
      refetch();
    },
    [refetch, revertIssueStepMutate],
  );

  const CreateTaskButton = () => (
    <QButton
      variant="ghost"
      leftIcon="PlusCircle"
      onClick={() => {
        window.dispatchEvent(
          new CustomEvent('QCreateTask-open', {
            detail: {
              type: 'task-action',
              contextId: step.id,
              parentQri: buildEventQri(companyId, step.issue_id),
              maxDueDate: eventDueDate,
            },
          }),
        );
      }}
      isDisabled={!canCreateTask}
      data-metrics="step-header-create-task-button"
    >
      {DisplayStrings.CreateTask}
    </QButton>
  );

  const CompleteStepButton = () => {
    const { data: tasks } = DataProvider.useDataProvider<UserTask>();
    const openTasks = tasks ? tasks.filter((task: UserTask) => task.status === 'open').length : 0;
    return (
      <QButton
        variant="ghost"
        leftIcon="CheckCircle"
        onClick={() => setIsCompleteStepModalOpen(true)}
        isDisabled={!canCompleteTaskStep || openTasks > 0}
        data-metrics="step-header-complete-step-button"
      >
        {t('tasks.actions.completeStep')}
      </QButton>
    );
  };

  const RevertStepButton = () => (
    <QButton
      variant="ghost"
      leftIcon="CornerUpLeft"
      onClick={() => setIsRevertStepModalOpen(true)}
      isDisabled={!canRevertStep || isResolutionStepForApproval}
      data-metrics="step-header-revert-step-button"
    >
      {t('common.labels.revert')}
    </QButton>
  );

  const completeStepModalInput: DigitalSignatureInput = {
    headingText: t('tasks.actions.completeStep'),
    subText: t('tasks.modals.signOffToCompleteStep', { stepLabel: step.label }),
    submitButtonText: t('tasks.actions.completeStep'),
    successToastHeader: t('common.labels.stepCompleted'),
    successToastDescription: t('tasks.modals.stepWasMarkedComplete', { stepLabel: step.label }),
  };

  const revertStepModalInput: DigitalSignatureInput = {
    headingText: t('tasks.actions.revertStepCompletion'),
    subText: t('tasks.modals.signOffToRevertCompletion', { stepLabel: step.label }),
    submitButtonText: t('tasks.actions.revertStep'),
    successToastHeader: t('common.labels.stepReverted'),
    successToastDescription: t('tasks.modals.stepCompletionWasReverted', { stepLabel: step.label }),
  };

  return (
    <StepContainer>
      <PaginationProvider>
        <Sorting.DefaultSortingProvider sortByKey="order_by" persistenceKey={null}>
          <DataProvider.Remote
            queryFn={handleTasksListApiCall}
            queryKey={['userTasksList', companyId, stepId, offset, limit]}
            queryParamKeys={{
              searchTerm: 'query',
              sortBy: 'order_by',
              pageSize: 'limit',
              pageIndex: 'offset',
            }}
          >
            <TaskContent
              step={step}
              stepStatus={stepStatus}
              stepNotCompleted={stepNotCompleted}
              isEventOwner={isEventOwner}
              onCompleteTask={onCompleteTask}
              onReopenTask={onReopenTask}
              onOpenTask={handleOpenTask}
              createTaskButton={<CreateTaskButton />}
              completeStepButton={<CompleteStepButton />}
              revertStepButton={<RevertStepButton />}
            />
            <DigitalSignatureModal
              isOpen={isCompleteStepModalOpen}
              setIsOpen={setIsCompleteStepModalOpen}
              onSave={handleCompleteStep}
              inputTexts={completeStepModalInput}
              isCommentRequired={false}
            />
            <DigitalSignatureModal
              isOpen={isRevertStepModalOpen}
              setIsOpen={setIsRevertStepModalOpen}
              onSave={handleRevertStep}
              inputTexts={revertStepModalInput}
              isCommentRequired={false}
            />
          </DataProvider.Remote>
        </Sorting.DefaultSortingProvider>
      </PaginationProvider>
    </StepContainer>
  );
};

export default TasksStep;
